/**
 * Raster intersection-over-union for proposal matching (§3.2, §9.4).
 *
 * Both polygons are scan-converted into a 192×108 mask (the plate's aspect,
 * one cell ≈ 0.5 % of width) and the IoU is a simple cell count. Rasterising
 * sidesteps every degenerate-geometry problem an analytic clipper has with
 * hand-drawn rings, and 20 k cells is nothing.
 */
import type { Poly, Ring } from './types'

export const IOU_W = 192
export const IOU_H = 108

/** Even-odd scanline fill of a ring into `mask` (1 = inside). */
function fillRing(mask: Uint8Array, ring: Ring, w: number, h: number, value: 0 | 1, xor: boolean): void {
  const n = ring.length
  if (n < 3) return
  const sx = w / 100
  const sy = h / 100
  const xs: number[] = []
  for (let row = 0; row < h; row++) {
    const y = (row + 0.5) / sy
    xs.length = 0
    for (let i = 0, j = n - 1; i < n; j = i++) {
      const a = ring[i]!
      const b = ring[j]!
      if (a[1] > y !== b[1] > y) {
        xs.push((a[0] + ((y - a[1]) * (b[0] - a[0])) / (b[1] - a[1])) * sx)
      }
    }
    if (xs.length < 2) continue
    xs.sort((p, q) => p - q)
    for (let k = 0; k + 1 < xs.length; k += 2) {
      const x0 = Math.max(0, Math.ceil(xs[k]! - 0.5))
      const x1 = Math.min(w - 1, Math.floor(xs[k + 1]! - 0.5))
      const base = row * w
      for (let x = x0; x <= x1; x++) {
        if (xor) mask[base + x] = mask[base + x] ? 0 : 1
        else mask[base + x] = value
      }
    }
  }
}

/** Rasterise a polygon (outer minus holes) into a `w×h` mask. */
export function rasterize(poly: Poly, w = IOU_W, h = IOU_H): Uint8Array {
  const mask = new Uint8Array(w * h)
  fillRing(mask, poly.outer, w, h, 1, false)
  for (const hole of poly.holes ?? []) fillRing(mask, hole, w, h, 0, false)
  return mask
}

export function maskIoU(a: Uint8Array, b: Uint8Array): number {
  let inter = 0
  let union = 0
  const n = Math.min(a.length, b.length)
  for (let i = 0; i < n; i++) {
    const x = a[i]!
    const y = b[i]!
    if (x && y) inter++
    if (x || y) union++
  }
  return union === 0 ? 0 : inter / union
}

/** IoU of two polygons at the 192×108 raster. `0` when either is empty. */
export function iou(a: Poly, b: Poly, w = IOU_W, h = IOU_H): number {
  return maskIoU(rasterize(a, w, h), rasterize(b, w, h))
}

/** Fraction of the mask that is filled (0..1) — the walkable-area rule uses this. */
export function coverage(mask: Uint8Array): number {
  let n = 0
  for (let i = 0; i < mask.length; i++) if (mask[i]) n++
  return mask.length === 0 ? 0 : n / mask.length
}
